import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Dropdown, Modal, Tooltip, message } from '@/ui/compat/antd';
import {
  CheckCircleOutlined,
  PrinterOutlined,
  DownOutlined,
  FilePdfOutlined,
  CloseCircleOutlined,
  StopOutlined,
  DownloadOutlined,
} from '@/ui/compat/icons';
import { Trash2 } from 'lucide-react';
import AmicaleValidationApi from '../../../api/AmicaleValidationApi';
import ReservationApi from '../../../api/ReservationApi';
import FactureApi from '../../../api/FactureApi';
import { reservationsApi as caReservationsApi } from '../../admin/modules/GestionCartesAccesEtBienEtre/api';
import { printAmicalQuote } from '../../admin/modules/GestionHotel/MoteurReservation/components/AmicalReservationModal/printAmicalQuote';
import {
  buildCaQuoteParamsFromReservation,
  downloadAmicalCaQuote,
  printAmicalCaQuote,
} from './printAmicalCaQuote';
import { AMICALE_VALIDATION_TAB } from './AmicaleValidationProductTabs';

const safeJson = (raw) => {
  if (!raw) return null;
  if (typeof raw !== 'string') return raw;
  try {
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

const buildQuoteParams = (reservation, voucherEmail) => {
  const guestDetails = safeJson(reservation.guest_details) || {};
  const hotelInfo = guestDetails.hotel_info || {};
  const roomsConfig = (hotelInfo.rooms || []).map((r) => ({
    room_type: r.room_type,
    arrangement: r.arrangement,
    adults: r.adults,
    children: r.children,
    id: r.id_hotel_chambre,
  }));

  return {
    hotel: {
      id: reservation.id_hotel,
      name: reservation.hotel_name || hotelInfo.name,
      ville_name: reservation.ville_name,
      pays_name: reservation.pays_name,
    },
    client: { name: reservation.client_name },
    amicalForm: {
      civilite_amical: reservation.civilite_amical,
      matricule_fiscal_amical: reservation.matricule_fiscal_amical,
      telephone_amical: reservation.telephone_amical,
    },
    voucherEmail,
    checkin: reservation.check_in,
    checkout: reservation.check_out,
    totalPrice: reservation.total_amount,
    currency: reservation.currency || 'TND',
    roomsConfig,
    guestDetails,
    reservationId: reservation.id_booking || reservation.id,
  };
};

const printVoucherInIframe = (html) => {
  const iframe = document.createElement('iframe');
  iframe.style.position = 'fixed';
  iframe.style.right = '0';
  iframe.style.bottom = '0';
  iframe.style.width = '0';
  iframe.style.height = '0';
  iframe.style.border = '0';
  document.body.appendChild(iframe);

  iframe.onload = () => {
    try {
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
    } catch (err) {
      console.error('voucher print failed:', err);
    }
    setTimeout(() => {
      try {
        if (iframe.parentNode) document.body.removeChild(iframe);
      } catch {
        /* noop */
      }
    }, 1500);
  };

  const doc = iframe.contentDocument || iframe.contentWindow.document;
  doc.open();
  doc.write(html);
  doc.close();
};


const formatTnd = (n) =>
  Number.isFinite(Number(n))
    ? Number(n).toLocaleString('fr-FR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    : '0,00';

/**
 * Mirrors the backend `resolvePaymentMethodForAmount` rule:
 *   1. DEPOSIT alone covers it  → deposit
 *   2. DEBITEUR alone covers it → debiteur
 *   3. DEPOSIT + DEBITEUR combined covers it → combined
 *   4. Else: insufficient, validation must be blocked.
 */
const resolveSoldeAvailability = (balance, amount) => {
  const need = Number(amount) || 0;
  const deposit = Number(balance?.deposit?.availableAmount) || 0;
  const debiteur = Number(balance?.plafond?.availableAmount) || 0;

  if (need <= 0)
    return { ok: true, source: 'deposit', deposit, debiteur, amount: need };
  if (deposit >= need)
    return { ok: true, source: 'deposit', deposit, debiteur, amount: need };
  if (debiteur >= need)
    return { ok: true, source: 'debiteur', deposit, debiteur, amount: need };
  if (deposit + debiteur >= need)
    return { ok: true, source: 'combined', deposit, debiteur, amount: need };
  return { ok: false, source: null, deposit, debiteur, amount: need };
};

const AmicaleReservationActions = ({
  reservation,
  productType = AMICALE_VALIDATION_TAB.HOTEL,
  voucherEmail,
  amicalClient,
  onValidated,
  onRejected,
  onCancelled,
  factureByReservation = {},
  clientId,
  balance,
}) => {
  const { t } = useTranslation();
  const isHotel = productType === AMICALE_VALIDATION_TAB.HOTEL;
  const factureKey = isHotel ? `hotel:${reservation.id}` : `ca:${reservation.id}`;
  const [validating, setValidating] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [cancelRecapVisible, setCancelRecapVisible] = useState(false);
  const [cancelRecapLoading, setCancelRecapLoading] = useState(false);
  const [cancelRecapSummary, setCancelRecapSummary] = useState(null);
  const [factureOverride, setFactureOverride] = useState(null);
  const [factureSyncing, setFactureSyncing] = useState(false);
  const cancelled = reservation.voucher_status === 'annulé';
  const voucherOnRequest = reservation.voucher_status === 'sur demande';
  const rejected = Number(reservation.is_amical_rejected) === 1 && !cancelled;
  const validated = Number(reservation.is_amical_validated) === 1 && !rejected && !cancelled;
  const statusLower = String(reservation.status || '').toLowerCase();
  const isCaOrBe = !isHotel;
  const isBackendConfirmed = statusLower === 'confirmed' || statusLower === 'used';
  // CA/BE stay "pending" in back-office after amical validation; amical validation is enough.
  const canAccessVoucherAndBl =
    validated && (isHotel || isBackendConfirmed || isCaOrBe);
  const factureForRow = factureByReservation[factureKey];
  const effectiveFacture = factureOverride || factureForRow;

  const sleep = (ms) => new Promise((r) => setTimeout(r, ms));

  const syncFactureForReservation = async () => {
    if (!clientId || !reservation?.id) return null;
    if (effectiveFacture?.id) return effectiveFacture;

    setFactureSyncing(true);
    try {
      // Backend creates facturation asynchronously; retry briefly to avoid forcing a full refresh.
      for (let attempt = 0; attempt < 4; attempt += 1) {
        const factures = await FactureApi.getFacturesByClient(clientId);
        const found = (factures || []).find(
          (f) =>
            f?.type_facture === 'client' &&
            (isHotel
              ? Number(f?.id_reservation) === Number(reservation.id)
              : Number(f?.id_ca_reservation) === Number(reservation.id)) &&
            f?.id
        );
        if (found) {
          setFactureOverride(found);
          return found;
        }
        await sleep(700);
      }
      return null;
    } finally {
      setFactureSyncing(false);
    }
  };

  const reservationAmountTnd =
    Number(reservation.montant_en_dinar) ||
    Number(reservation.total_amount) ||
    0;

  const soldeCheck = useMemo(
    () => resolveSoldeAvailability(balance, reservationAmountTnd),
    [balance, reservationAmountTnd]
  );

  const balanceLoaded = !!balance;
  const canValidate = balanceLoaded ? soldeCheck.ok : false;

  const handleValidate = () => {
    const currency = reservation.currency || 'TND';
    const amount = formatTnd(reservation.total_amount);
    const sourceLabel =
      soldeCheck.source === 'deposit'
        ? t('amicales.actions.paymentSourceDeposit')
        : soldeCheck.source === 'debiteur'
          ? t('amicales.actions.paymentSourceDebiteur')
          : t('amicales.actions.paymentSourceCombined', {
              deposit: formatTnd(soldeCheck.deposit),
              debiteurPart: formatTnd(Number(soldeCheck.amount) - soldeCheck.deposit),
            });
    Modal.confirm({
      title: t('amicales.actions.confirmValidateTitle'),
      content: t('amicales.actions.confirmValidateContent', {
        sourceLabel,
        amount,
        currency,
      }),
      okText: t('amicales.actions.validate'),
      cancelText: t('common.cancel'),
      onOk: async () => {
        setValidating(true);
        try {
          await AmicaleValidationApi.validateReservation(reservation.id, productType);
          message.success(t('amicales.actions.validateSuccess'));
          onValidated?.(reservation.id);
          // Preload facture so "Facture" works immediately after validation.
          await syncFactureForReservation();
        } catch (err) {
          const msg =
            err?.response?.data?.message ||
            err?.response?.data?.details ||
            err?.message ||
            t('amicales.actions.validateError');
          message.error(msg);
        } finally {
          setValidating(false);
        }
      },
    });
  };

  const handleReject = () => {
    Modal.confirm({
      title: t('amicales.actions.confirmRejectTitle'),
      content: t('amicales.actions.confirmRejectContent', {
        ref: reservation.id_booking || reservation.id,
      }),
      okText: t('amicales.actions.confirmRejectOk'),
      okButtonProps: { danger: true },
      cancelText: t('common.cancel'),
      onOk: async () => {
        setRejecting(true);
        try {
          await AmicaleValidationApi.rejectReservation(reservation.id, productType);
          message.success(t('amicales.actions.rejectSuccess'));
          onRejected?.(reservation.id);
        } catch (err) {
          message.error(err?.response?.data?.message || t('amicales.actions.rejectError'));
        } finally {
          setRejecting(false);
        }
      },
    });
  };

  const handleOpenCancelRecap = async () => {
    setCancelRecapSummary(null);
    setCancelRecapVisible(true);
    setCancelRecapLoading(true);
    try {
      const summary = await ReservationApi.getCancellationSummary(reservation.id);
      setCancelRecapSummary(summary);
    } catch {
      setCancelRecapSummary({ error: true });
    } finally {
      setCancelRecapLoading(false);
    }
  };

  const handleConfirmCancel = async () => {
    setCancelRecapVisible(false);
    setCancelling(true);
    try {
      const isExternal = reservation.source === 'cyberesa' || reservation.source === 'octasoft';
      if (isExternal) {
        const response = await ReservationApi.cancelExternalBooking(reservation.id, { languageId: 1 });
        if (!response.success) throw new Error(response.message || t('amicales.actions.cancelFailed'));
      } else {
        await ReservationApi.annulerReservation(reservation.id);
      }
      message.success(
        t('amicales.actions.cancelSuccess', { ref: reservation.id_booking || reservation.id })
      );
      onCancelled?.(reservation.id);
    } catch (err) {
      message.error(err?.response?.data?.message || err?.message || t('amicales.actions.cancelError'));
    } finally {
      setCancelling(false);
      setCancelRecapSummary(null);
    }
  };

  const handlePrintQuote = async () => {
    try {
      await printAmicalQuote(buildQuoteParams(reservation, voucherEmail));
    } catch (err) {
      message.error(t('amicales.actions.printQuoteError'));
    }
  };

  const buildCaQuoteParams = () =>
    buildCaQuoteParamsFromReservation(reservation, { amicalClient, voucherEmail });

  const handlePrintCaQuote = async () => {
    try {
      await printAmicalCaQuote(buildCaQuoteParams());
    } catch {
      message.error(t('amicales.actions.printQuoteError'));
    }
  };

  const handleDownloadCaQuote = async () => {
    try {
      await downloadAmicalCaQuote(buildCaQuoteParams());
    } catch {
      message.error(t('amicales.actions.downloadQuoteError'));
    }
  };

  const handlePrintVoucher = async () => {
    if (!canAccessVoucherAndBl) {
      message.warning(t('amicales.actions.voucherAfterValidation'));
      return;
    }
    if (voucherOnRequest) {
      message.warning(t('amicales.actions.voucherOnRequestOnly'));
      return;
    }
    try {
      if (isHotel) {
        let html = await ReservationApi.generateHtmlVoucher(reservation.id, 'fr');
        if (html && typeof html === 'object') {
          html = html.html || html.content || '';
        }
        if (!html) { message.warning(t('amicales.actions.voucherUnavailable')); return; }
        printVoucherInIframe(html);
        return;
      }
      const html = await caReservationsApi.fetchVoucherHtml(reservation.id, 'fr');
      if (!html) { message.warning(t('amicales.actions.voucherUnavailable')); return; }
      printVoucherInIframe(html);
    } catch (err) {
      message.error(err?.response?.data?.message || t('amicales.actions.voucherError'));
    }
  };

  const handlePrintFacture = async () => {
    if (!canAccessVoucherAndBl) {
      message.warning(t('amicales.actions.deliveryNoteAfterValidation'));
      return;
    }
    if (isHotel && voucherOnRequest) {
      message.warning(t('amicales.actions.deliveryNoteOnRequestOnly'));
      return;
    }
    let facture = effectiveFacture;
    if (!facture?.id && validated) {
      facture = await syncFactureForReservation();
    }
    if (!facture?.id) {
      message.warning(
        factureSyncing
          ? t('amicales.actions.invoiceGenerating')
          : t('amicales.actions.invoiceNotReady')
      );
      return;
    }
    try {
      const response = await FactureApi.generateHtmlBill(facture.id, 'fr');
      const html = response?.html || response;
      if (!html) { message.warning(t('amicales.actions.invoiceUnavailable')); return; }
      printVoucherInIframe(html);
    } catch (err) {
      console.log('amicales:AmicaleReservationActions L284: err', err);
      message.error(err?.response?.data?.message || t('amicales.actions.invoiceError'));
    }
  };

  const items = [
    ...(isHotel
      ? [{ key: 'quote-print', icon: <PrinterOutlined />, label: t('amicales.actions.menuQuote') }]
      : [
          {
            key: 'ca-quote-download',
            icon: <DownloadOutlined />,
            label: t('amicales.actions.menuDownloadQuote'),
          },
          {
            key: 'ca-quote-print',
            icon: <PrinterOutlined />,
            label: t('amicales.actions.menuPrintQuote'),
          },
        ]),
    {
      key: 'voucher-print',
      icon: <PrinterOutlined />,
      label: voucherOnRequest
        ? t('amicales.actions.menuVoucherOnRequest')
        : t('amicales.actions.menuVoucher'),
      disabled: !canAccessVoucherAndBl || voucherOnRequest,
    },
    {
      key: 'facture-print',
      icon: <FilePdfOutlined />,
      label:
        isHotel && voucherOnRequest
          ? t('amicales.actions.menuDeliveryNoteOnRequest')
          : t('amicales.actions.menuDeliveryNote'),
      disabled: !canAccessVoucherAndBl || (isHotel && voucherOnRequest),
    },
  ];

  const handleMenuClick = ({ key }) => {
    if (key === 'quote-print') handlePrintQuote();
    else if (key === 'ca-quote-download') handleDownloadCaQuote();
    else if (key === 'ca-quote-print') handlePrintCaQuote();
    else if (key === 'voucher-print') handlePrintVoucher();
    else if (key === 'facture-print') handlePrintFacture();
  };

  const tooltipMessage = !balanceLoaded
    ? t('amicales.actions.balanceLoading')
    : !canValidate
      ? t('amicales.actions.balanceInsufficient', {
          deposit: formatTnd(soldeCheck.deposit),
          ceiling: formatTnd(soldeCheck.debiteur),
          amount: formatTnd(soldeCheck.amount),
        })
      : '';

  return (
    <>
      <div className='flex flex-wrap gap-2 justify-end items-center'>
        {cancelled ? (
          <span className='inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700'>
            <StopOutlined /> {t('amicales.actions.cancelled')}
          </span>
        ) : rejected ? (
          <span className='inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700'>
            <CloseCircleOutlined /> {t('amicales.actions.rejected')}
          </span>
        ) : !validated ? (
          <>
            <Tooltip title={tooltipMessage || ''}>
              <span>
                <Button
                  type='primary'
                  icon={<CheckCircleOutlined />}
                  loading={validating}
                  onClick={handleValidate}
                  disabled={!canValidate}
                  size='small'
                  style={canValidate ? { background: '#16a34a', borderColor: '#16a34a' } : undefined}
                >
                  {t('amicales.actions.validate')}
                </Button>
              </span>
            </Tooltip>
            <Button
              danger
              icon={<CloseCircleOutlined />}
              loading={rejecting}
              onClick={handleReject}
              size='small'
            >
              {t('amicales.actions.reject')}
            </Button>
          </>
        ) : (
          <>
            <span className='inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800'>
              <CheckCircleOutlined /> {t('amicales.actions.validated')}
            </span>
            {isHotel ? (
              <Button
                danger
                icon={<Trash2 size={12} className={cancelling ? 'animate-spin' : ''} />}
                loading={cancelling}
                onClick={handleOpenCancelRecap}
                size='small'
                disabled={cancelling}
              >
                {cancelling ? t('amicales.actions.cancelling') : t('amicales.actions.cancel')}
              </Button>
            ) : null}
          </>
        )}

        <Dropdown
          menu={{ items, onClick: handleMenuClick }}
          trigger={['click']}
          placement='bottomRight'
          disabled={cancelled || rejected}
        >
          <Button size='small' disabled={cancelled || rejected}>
            {t('amicales.actions.actionsMenu')} <DownOutlined />
          </Button>
        </Dropdown>
      </div>

      <Modal
        title={t('amicales.actions.confirmCancelTitle')}
        open={cancelRecapVisible}
        onCancel={() => { setCancelRecapVisible(false); setCancelRecapSummary(null); }}
        width={520}
        footer={[
          <Button key='no' onClick={() => { setCancelRecapVisible(false); setCancelRecapSummary(null); }}>
            {t('common.no')}
          </Button>,
          <Button
            key='yes'
            type='primary'
            danger
            onClick={handleConfirmCancel}
            disabled={cancelRecapLoading || cancelRecapSummary?.error}
          >
            {t('amicales.actions.confirmCancelYes')}
          </Button>,
        ]}
      >
        {cancelRecapLoading ? (
          <div className='py-6 text-center text-gray-500'>{t('amicales.actions.cancelRecapLoading')}</div>
        ) : cancelRecapSummary?.error ? (
          <div className='py-4 text-gray-600'>{t('amicales.actions.cancelRecapError')}</div>
        ) : cancelRecapSummary ? (
          <div className='space-y-3 text-sm'>
            <p className='text-gray-600'>
              {t('amicales.actions.cancelRecapReservation', {
                ref: reservation.id_booking || reservation.id,
              })}
            </p>
            {cancelRecapSummary.penaltyAmount > 0 && (
              <p>
                <strong>{t('amicales.actions.cancelRecapPenalty')}</strong>{' '}
                {Number(cancelRecapSummary.penaltyAmount).toLocaleString('fr-FR', {
                  minimumFractionDigits: 2,
                })}{' '}
                TND
              </p>
            )}
          </div>
        ) : null}
      </Modal>
    </>
  );
};

export default AmicaleReservationActions;
